<template>
    <component
        :is="componentType"
        :to="to"
        :disabled="isDisabled"
        :class="[buttonClasses, $attrs.class]"
        :style="variant === 'secondary' ? null : skewStyle"
        @click="handleClick"
    >
        <template v-if="variant === 'secondary'">
            <div
                class="absolute inset-0"
                :class="isDisabled ? 'bg-card' : borderColor"
                :style="skewStyle"
            ></div>
            <div
                class="absolute inset-px"
                :class="[
                    isDisabled ? 'bg-card' : fillColor,
                    !isDisabled && 'hover:bg-card-1 transition-colors',
                ]"
                :style="skewStyle"
            ></div>
        </template>

        <div
            class="mt-0.5 min-w-0 text-sm leading-none tracking-tight"
            :class="[
                variant === 'secondary'
                    ? 'relative z-10 flex items-center space-x-1'
                    : 'flex items-center space-x-1',
                padding,
            ]"
        >
            <span
                v-if="loading"
                class="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            ></span>
            <slot />
        </div>
    </component>
</template>

<script>
export default {
    name: 'DiagonalCutButton',
    props: {
        to: {
            type: [String, Object],
            default: null,
        },
        size: {
            type: String,
            default: 'default',
            validator: (value) => ['small', 'default', 'large'].includes(value),
        },
        padding: {
            type: String,
            default: 'p-1.25',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        fullWidth: {
            type: Boolean,
            default: true,
        },
        variant: {
            type: String,
            default: 'primary',
            validator: (value) => ['primary', 'secondary', 'secondary-white'].includes(value),
        },
        cutPosition: {
            type: String,
            default: 'left-top',
            validator: (value) => ['left-top', 'right-top', 'both-top'].includes(value),
        },
        borderColor: {
            type: String,
            default: 'bg-zinc-700',
        },
        fillColor: {
            type: String,
            default: 'bg-zinc-900',
        },
    },
    emits: ['click'],
    computed: {
        componentType() {
            return this.to ? 'RouterLink' : 'button'
        },
        isDisabled() {
            return this.disabled || this.loading
        },
        sizeClasses() {
            const sizes = {
                small: 'px-3 py-2 text-sm',
                default: 'py-2',
                large: 'px-6 py-5 text-lg',
            }
            return sizes[this.size]
        },
        variantClasses() {
            if (this.isDisabled) {
                return 'bg-card text-titles cursor-not-allowed'
            }

            const variants = {
                primary: 'bg-red-600 text-white hover:bg-red-700 ',
                secondary: 'text-white relative',
                'secondary-white': 'text-black bg-white relative',
            }
            return variants[this.variant]
        },
        buttonClasses() {
            return [
                'font-semibold whitespace-nowrap px-3 text-center inline-flex uppercase items-center justify-center relative',
                this.fullWidth ? 'block w-full' : '',
                this.sizeClasses,
                this.variantClasses,
            ]
        },
        skewStyle() {
            const cuts = {
                'left-top': 'polygon(10px 0, 100% 0, 100% 100%, 0 100%, 0 10px)',
                'right-top': 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 0 100%)',
                'both-top':
                    'polygon(10px 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 0 100%, 0 10px)',
            }
            return {
                clipPath: cuts[this.cutPosition],
            }
        },
    },
    methods: {
        handleClick(event) {
            if (!this.isDisabled) {
                this.$emit('click', event)
            }
        },
    },
}
</script>
